/**
 * ElevenLabs Text-to-Speech Service
 * Handles API calls to ElevenLabs for generating speech from text
 */

const ELEVENLABS_API_KEY = process.env.REACT_APP_ELEVENLABS_API_KEY;
const ELEVENLABS_API_URL = 'https://api.elevenlabs.io/v1';

// Log API key fingerprint for debugging (first 8 and last 4 chars)
if (ELEVENLABS_API_KEY) {
  const keyLength = ELEVENLABS_API_KEY.length;
  const fingerprint = `${ELEVENLABS_API_KEY.substring(0, 8)}...${ELEVENLABS_API_KEY.substring(keyLength - 4)}`;
  console.log('[ELEVENLABS] 🔑 API Key loaded:', {
    configured: true,
    keyLength: keyLength,
    fingerprint: fingerprint,
    note: 'Verify this matches your new API key in Netlify'
  });
} else {
  console.warn('[ELEVENLABS] ⚠️ API Key not configured');
}

// Default voice ID - can be changed via configuration
// Using "Rachel" - a clear, professional female voice
// You can get available voices from: https://api.elevenlabs.io/v1/voices
const DEFAULT_VOICE_ID = '21m00Tcm4TlvDq8ikWAM'; // Rachel voice

// Cache for audio files to avoid regenerating the same text
const audioCache = new Map();

/**
 * Get available voices from ElevenLabs
 */
export const getAvailableVoices = async () => {
  if (!ELEVENLABS_API_KEY) {
    console.warn('ElevenLabs API key not configured');
    return [];
  }

  try {
    const response = await fetch(`${ELEVENLABS_API_URL}/voices`, {
      method: 'GET',
      headers: {
        'xi-api-key': ELEVENLABS_API_KEY,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch voices: ${response.statusText}`);
    }

    const data = await response.json();
    return data.voices || [];
  } catch (error) {
    console.error('Error fetching voices:', error);
    return [];
  }
};

/**
 * Generate speech from text using ElevenLabs API
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - Voice ID to use (optional, defaults to DEFAULT_VOICE_ID)
 * @param {object} options - Additional options (model, stability, similarity_boost, etc.)
 * @returns {Promise<Blob>} Audio blob
 */
export const generateSpeech = async (text, voiceId = DEFAULT_VOICE_ID, options = {}) => {
  if (!ELEVENLABS_API_KEY) {
    throw new Error('ElevenLabs API key not configured. Please set REACT_APP_ELEVENLABS_API_KEY in your .env file');
  }

  if (!text || text.trim() === '') {
    throw new Error('Text cannot be empty');
  }

  // Check cache first
  const cacheKey = `${voiceId}_${text}`;
  if (audioCache.has(cacheKey)) {
    console.log('Using cached audio for:', text.substring(0, 50));
    return audioCache.get(cacheKey);
  }

  // Log the exact text being sent to ElevenLabs
  console.log('[ELEVENLABS] 📤 Sending text to API:', {
    textLength: text.length,
    textPreview: text.substring(0, 200),
    textEnd: text.substring(Math.max(0, text.length - 100)),
    completeText: text, // Log complete text to verify no truncation
    wordCount: text.split(/\s+/).length
  });

  try {
    const requestBody = {
      text: text, // Ensure we're using the full text, not truncated
      // Using eleven_multilingual_v2 for highest quality, smoothest voice (better than turbo for natural delivery)
      // Falls back to eleven_monolingual_v1 if multilingual not available, then eleven_turbo_v2_5
      model_id: options.model || 'eleven_multilingual_v2',
      voice_settings: {
        // Higher stability (0.75-0.8) = smoother, more consistent delivery, less variation
        stability: options.stability !== undefined ? options.stability : 0.75,
        // Higher similarity_boost (0.9) = more consistent voice characteristics, less choppy
        similarity_boost: options.similarity_boost !== undefined ? options.similarity_boost : 0.9,
        style: options.style !== undefined ? options.style : 0.0,
        // Speaker boost enhances clarity and naturalness
        use_speaker_boost: options.use_speaker_boost !== false,
      },
    };
    
    // Verify text in request body
    console.log('[ELEVENLABS] 📋 Request body text length:', requestBody.text.length);
    console.log('[ELEVENLABS] 📋 Request body text (first 200):', requestBody.text.substring(0, 200));
    console.log('[ELEVENLABS] 📋 Request body text (last 100):', requestBody.text.substring(Math.max(0, requestBody.text.length - 100)));
    console.log('[ELEVENLABS] 🎙️ Voice settings:', {
      model: requestBody.model_id,
      stability: requestBody.voice_settings.stability,
      similarity_boost: requestBody.voice_settings.similarity_boost,
      use_speaker_boost: requestBody.voice_settings.use_speaker_boost
    });
    
    // Try the preferred model first, fallback to alternatives if needed
    const modelsToTry = [
      requestBody.model_id, // Try preferred model first
      'eleven_monolingual_v1', // High quality monolingual fallback
      'eleven_turbo_v2_5', // Fast fallback
    ];
    
    let lastError = null;
    let audioBlob = null;
    
    for (const model of modelsToTry) {
      try {
        const requestBodyWithModel = {
          ...requestBody,
          model_id: model,
        };
        
        console.log(`[ELEVENLABS] 🎯 Attempting with model: ${model}`);
        
        console.log(`[ELEVENLABS] 🌐 Starting fetch request to ElevenLabs API...`);
        const fetchStartTime = Date.now();
        
        // Add timeout wrapper to prevent hanging - use AbortController for proper cleanup
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error(`[ELEVENLABS] ⏱️ Fetch timeout triggered after 30 seconds for model ${model}`);
          controller.abort();
        }, 30000);
        
        let response;
        try {
          console.log(`[ELEVENLABS] 🌐 Fetching from: ${ELEVENLABS_API_URL}/text-to-speech/${voiceId}`);
          
          response = await fetch(
            `${ELEVENLABS_API_URL}/text-to-speech/${voiceId}`,
            {
              method: 'POST',
              headers: {
                'Accept': 'audio/mpeg',
                'Content-Type': 'application/json',
                'xi-api-key': ELEVENLABS_API_KEY,
              },
              body: JSON.stringify(requestBodyWithModel),
              signal: controller.signal,
            }
          );
          
          clearTimeout(timeoutId);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          
          // Check if it was an abort due to timeout
          if (fetchError.name === 'AbortError') {
            console.error(`[ELEVENLABS] ⏱️ Request aborted (timeout) for model ${model}`);
            lastError = new Error(`Request timeout after 30 seconds for model ${model}`);
          } else {
            console.error(`[ELEVENLABS] ❌ Fetch error for model ${model}:`, {
              name: fetchError.name,
              message: fetchError.message,
              stack: fetchError.stack
            });
            lastError = fetchError;
          }
          continue; // Try next model
        }

        const fetchTime = Date.now() - fetchStartTime;
        console.log(`[ELEVENLABS] 📡 Fetch completed in ${fetchTime}ms, status: ${response.status} ${response.statusText}`);

        if (response.ok) {
          console.log(`[ELEVENLABS] ✅ Response OK, reading blob...`);
          const blobStartTime = Date.now();
          
          // Also add timeout to blob reading
          const blobTimeoutId = setTimeout(() => {
            console.error(`[ELEVENLABS] ⏱️ Blob reading timeout after 30 seconds`);
          }, 30000);
          
          try {
            audioBlob = await response.blob();
            clearTimeout(blobTimeoutId);
          } catch (blobError) {
            clearTimeout(blobTimeoutId);
            console.error(`[ELEVENLABS] ❌ Error reading blob for model ${model}:`, blobError);
            lastError = blobError;
            continue; // Try next model
          }
          
          const blobTime = Date.now() - blobStartTime;
          console.log(`[ELEVENLABS] ✅ Successfully generated audio with model: ${model}`, {
            blobSize: audioBlob.size,
            blobSizeKB: (audioBlob.size / 1024).toFixed(2),
            blobType: audioBlob.type,
            totalTime: Date.now() - fetchStartTime,
            blobReadTime: blobTime
          });
          break; // Success, exit loop
        } else {
          let errorText = '';
          try {
            errorText = await response.text();
          } catch (e) {
            errorText = 'Could not read error response';
          }
          console.error(`[ELEVENLABS] ❌ Model ${model} failed:`, {
            status: response.status,
            statusText: response.statusText,
            errorText: errorText.substring(0, 200),
            fetchTime: fetchTime
          });
          lastError = new Error(`ElevenLabs API error: ${response.status} - ${errorText}`);
          console.log(`[ELEVENLABS] ⚠️ Model ${model} failed, trying next...`);
          // Continue to next model
        }
      } catch (error) {
        lastError = error;
        console.error(`[ELEVENLABS] ❌ Model ${model} exception:`, {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause
        });
        // Continue to next model
      }
    }
    
    if (!audioBlob) {
      throw lastError || new Error('Failed to generate speech with all available models');
    }

    // Cache the audio blob
    audioCache.set(cacheKey, audioBlob);
    
    return audioBlob;
  } catch (error) {
    console.error('Error generating speech:', error);
    throw error;
  }
};

/**
 * Generate speech and return as audio URL
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - Voice ID to use (optional)
 * @param {object} options - Additional options
 * @returns {Promise<string>} Object URL for the audio blob
 */
export const generateSpeechUrl = async (text, voiceId = DEFAULT_VOICE_ID, options = {}) => {
  const audioBlob = await generateSpeech(text, voiceId, options);
  return URL.createObjectURL(audioBlob);
};

/**
 * Stream speech for real-time playback (using streaming API)
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - Voice ID to use (optional)
 * @param {function} onChunk - Callback for each audio chunk
 * @param {object} options - Additional options
 */
export const streamSpeech = async (text, voiceId = DEFAULT_VOICE_ID, onChunk, options = {}) => {
  if (!ELEVENLABS_API_KEY) {
    throw new Error('ElevenLabs API key not configured');
  }

  try {
    const response = await fetch(
      `${ELEVENLABS_API_URL}/text-to-speech/${voiceId}/stream`,
      {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': ELEVENLABS_API_KEY,
        },
        body: JSON.stringify({
          text: text,
          model_id: options.model || 'eleven_multilingual_v2', // Highest quality model for smooth, natural voice
          voice_settings: {
            stability: options.stability !== undefined ? options.stability : 0.75, // Higher for smoother delivery
            similarity_boost: options.similarity_boost !== undefined ? options.similarity_boost : 0.9, // Higher for consistent voice
            style: options.style !== undefined ? options.style : 0.0,
            use_speaker_boost: options.use_speaker_boost !== false,
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`ElevenLabs API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      chunks.push(value);
      if (onChunk) {
        onChunk(value);
      }
    }

    // Combine all chunks into a single blob
    const audioBlob = new Blob(chunks, { type: 'audio/mpeg' });
    return audioBlob;
  } catch (error) {
    console.error('Error streaming speech:', error);
    throw error;
  }
};

/**
 * Clear the audio cache
 */
export const clearAudioCache = () => {
  audioCache.clear();
};

/**
 * Check if API key is configured
 */
export const isConfigured = () => {
  return !!ELEVENLABS_API_KEY;
};

